if (!customElements.get('search-suggestions')) {
  class SearchSuggestions extends HTMLElement {
    /**
     * SPEC-11 — Cache de resultados de busca preditiva (LRU simples, max 20).
     * Static pq queremos compartilhar entre instâncias (predictive-search e
     * main-search montam várias). Key = query normalizada (lowercase + trim).
     * Reduz fetches redundantes quando user digita "leg" → "legg" → backspace
     * → "leg" novamente. Esses backspace-loops são comuns em mobile.
     */
    static resultsCache = new Map();
    static MAX_CACHE_ENTRIES = 20;
    static MIN_QUERY_LENGTH = 2;

    static cacheGet(key) {
      const value = SearchSuggestions.resultsCache.get(key);
      if (value) {
        // LRU touch: re-insere pra mover pra ponta (Map preserva ordem de inserção)
        SearchSuggestions.resultsCache.delete(key);
        SearchSuggestions.resultsCache.set(key, value);
      }
      return value;
    }

    static cacheSet(key, value) {
      if (SearchSuggestions.resultsCache.has(key)) {
        SearchSuggestions.resultsCache.delete(key);
      } else if (SearchSuggestions.resultsCache.size >= SearchSuggestions.MAX_CACHE_ENTRIES) {
        // Remove o item mais antigo (primeira key na ordem de inserção)
        const oldestKey = SearchSuggestions.resultsCache.keys().next().value;
        SearchSuggestions.resultsCache.delete(oldestKey);
      }
      SearchSuggestions.resultsCache.set(key, value);
    }

    connectedCallback() {
      this.searchTimeoutID = null;
      this.autoSubmitTimeoutID = null;
      this.abortController = null;

      const form = this.closest('form');
      if (!form) return;

      this.form = form;
      this.searchInput = form.querySelector('input[name="q"]');
      if (!this.searchInput) return;

      this.mainSearchRoot = this.closest('main-search') || this.closest('.main-search');
      this.closeBtn =
        (this.mainSearchRoot && this.mainSearchRoot.querySelector('.main-search__close')) ||
        document.querySelector('.main-search__close');

      this.injectStyles();
      this.patchOverflowParents();

      // ✅ buscar (sugestões)
      this.searchInput.addEventListener('input', this.onInput.bind(this));

      // ✅ resetar ao concluir pesquisa (Enter / submit)
      this.form.addEventListener('submit', this.onSubmit.bind(this));

      // ✅ resetar ao fechar modal (botão X)
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.resetSearchUI());
      }

      // ✅ resetar ao fechar via ESC
      document.addEventListener('keydown', this.onKeyDown.bind(this));

      // ✅ resetar quando o form sofre reset (caso tema use)
      this.form.addEventListener('reset', () => this.resetSearchUI());

      // ✅ resetar quando o modal for fechado por qualquer motivo e o input perder foco
      this.searchInput.addEventListener('blur', () => {
        // dá tempo do click no close acontecer antes do reset duplicado
        setTimeout(() => {
          // se o foco saiu do modal inteiro, reseta
          const active = document.activeElement;
          if (this.mainSearchRoot && active && this.mainSearchRoot.contains(active)) return;
          this.resetSearchUI();
        }, 180);
      });
    }

    /* =======================
       RESET UX (o que você pediu)
    ======================= */
    resetSearchUI() {
      // limpa input
      this.searchInput.value = '';

      // limpa sugestões
      this.innerHTML = '';

      // limpa timers + requests
      clearTimeout(this.searchTimeoutID);
      clearTimeout(this.autoSubmitTimeoutID);

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      // opcional: limpar resultados do container do tema, se existir
      const resultsBox = this.mainSearchRoot
        ? this.mainSearchRoot.querySelector('.main-search__results')
        : null;
      if (resultsBox) resultsBox.innerHTML = '';

      // dispara input event pra qualquer script do tema recalcular estado
      this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    onSubmit() {
      // deixa o submit acontecer e reseta logo em seguida
      setTimeout(() => this.resetSearchUI(), 50);
    }

    onKeyDown(e) {
      if (e.key === 'Escape') {
        this.resetSearchUI();
      }
    }

    /* =======================
       Styles + layout fixes (mantidos)
    ======================= */
    injectStyles() {
      if (document.getElementById('premium-search-suggestions-styles')) return;

      const style = document.createElement('style');
      style.id = 'premium-search-suggestions-styles';
      style.textContent = `
        search-suggestions {
          position: relative;
          z-index: 9999;
          display: block;
        }

        search-suggestions .search-suggestions-wrapper {
          position: relative;
          z-index: 9999;
        }

        search-suggestions .search-suggestions {
          margin-top: 10px;
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 16px 40px rgba(0,0,0,.10);
          border: 1px solid rgba(0,0,0,.06);
          max-height: min(520px, calc(100vh - 180px));
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
        }

        search-suggestions .search-suggestions__heading {
          padding: 12px 14px;
          font-size: 12px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: rgba(0,0,0,.55);
          background: rgba(0,0,0,.02);
          display: block;
        }

        search-suggestions .search-suggestions__results-list {
          list-style: none;
          margin: 0;
          padding: 8px;
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
        }

        @media (min-width: 768px) {
          search-suggestions .search-suggestions__results-list {
            grid-template-columns: 1fr 1fr;
          }
        }

        search-suggestions .search-suggestions__itemCard {
          display: grid;
          grid-template-columns: 64px 1fr;
          gap: 12px;
          align-items: center;
          padding: 10px 12px;
          border-radius: 12px;
          text-decoration: none;
          color: inherit;
          transition: background .12s ease, transform .12s ease;
          min-height: 72px;
        }

        search-suggestions .search-suggestions__itemCard:hover,
        search-suggestions .search-suggestions__itemCard:focus-visible {
          background: rgba(0,0,0,.03);
          transform: translateY(-1px);
          outline: none;
        }

        search-suggestions .search-suggestions__thumb {
          width: 64px;
          height: 64px;
          border-radius: 12px;
          background: rgba(0,0,0,.04);
          overflow: hidden;
          flex-shrink: 0;
        }

        search-suggestions .search-suggestions__thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        search-suggestions .search-suggestions__title {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        search-suggestions .search-suggestions__desc {
          margin-top: 6px;
          font-size: 12px;
          line-height: 1.35;
          color: rgba(0,0,0,.62);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        search-suggestions .search-suggestions__ctaRow {
          position: sticky;
          bottom: 0;
          padding: 12px;
          border-top: 1px solid rgba(0,0,0,.06);
          background: rgba(255,255,255,.96);
          backdrop-filter: blur(6px);
          z-index: 2;
        }

        search-suggestions .search-suggestions__cta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-decoration: none;
          font-weight: 500;
          color: inherit;
        }

        search-suggestions .search-suggestions__cta span {
          opacity: .6;
        }
      `;
      document.head.appendChild(style);
    }

    patchOverflowParents() {
      let el = this.form;
      for (let i = 0; i < 4; i += 1) {
        if (!el || !el.parentElement) break;
        el = el.parentElement;
        const cs = window.getComputedStyle(el);
        if (cs.overflow === 'hidden' || cs.overflowY === 'hidden') {
          el.style.overflow = 'visible';
          el.style.overflowY = 'visible';
        }
        if (cs.position === 'static') {
          el.style.position = 'relative';
        }
      }
    }

    /* =======================
       Search logic (imagem + descrição)
    ======================= */
    onInput(e) {
      const query = (e.target.value || '').trim();

      if (!query) {
        this.innerHTML = '';
        return;
      }

      clearTimeout(this.searchTimeoutID);
      clearTimeout(this.autoSubmitTimeoutID);
      if (this.abortController) this.abortController.abort();

      // SPEC-11: skip se query muito curta (1 char traz noise/sem qualidade)
      // EXCEÇÃO: SKU numérico (auto-submit logic depende de buscar mesmo curto)
      if (query.length < SearchSuggestions.MIN_QUERY_LENGTH && !/^\d+$/.test(query)) {
        this.innerHTML = '';
        return;
      }

      // SPEC-11: cache hit antes do debounce. Renderiza imediato sem fetch.
      const cacheKey = query.toLowerCase();
      const cached = SearchSuggestions.cacheGet(cacheKey);
      if (cached) {
        this.render(cached, query);
        return;
      }

      this.searchTimeoutID = setTimeout(() => {
        this.fetchResults(query);
      }, 250);
    }

    async fetchResults(query) {
      this.abortController = new AbortController();

      const base =
        window.theme?.routes?.predictiveSearch
          ? window.theme.routes.predictiveSearch
          : '/search/suggest';

      const url =
        `${base}.json?q=${encodeURIComponent(query)}` +
        `&resources[type]=product&resources[limit]=6` +
        `&resources[options][fields]=title,product_type,tag,variants.sku`;

      try {
        const res = await fetch(url, { signal: this.abortController.signal });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();

        const products = Array.isArray(data?.resources?.results?.products)
          ? data.resources.results.products
          : [];

        // SPEC-11: cache do resultado pra futuras queries iguais.
        // Cacheia mesmo array vazio (evita re-fetch de query inválida).
        SearchSuggestions.cacheSet(query.toLowerCase(), products);

        this.render(products, query);

        // SKU numérico: se predictive não retornar, auto-submit sem Enter
        if (products.length === 0 && /^\d+$/.test(query) && query.length >= 4) {
          const snapshot = query;
          this.autoSubmitTimeoutID = setTimeout(() => {
            if ((this.searchInput.value || '').trim() === snapshot) {
              this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
            }
          }, 500);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Search error', err);
          this.innerHTML = '';
        }
      }
    }

    getImage(p) {
      if (typeof p?.image === 'string') return p.image;
      if (p?.image?.url) return p.image.url;
      if (p?.featured_image?.url) return p.featured_image.url;
      return null;
    }

    strip(html) {
      return String(html || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    getDescription(p) {
      const candidates = [p?.description, p?.body, p?.content, p?.body_html];
      for (let i = 0; i < candidates.length; i += 1) {
        const text = this.strip(candidates[i]);
        if (text.length > 20) return text;
      }
      return p?.product_type || null;
    }

    render(products, query) {
      const wrapper = document.createElement('div');
      wrapper.className = 'search-suggestions-wrapper';

      const box = document.createElement('div');
      box.className = 'search-suggestions';

      const heading = document.createElement('span');
      heading.className = 'search-suggestions__heading';
      heading.textContent = 'Produtos';
      box.appendChild(heading);

      const list = document.createElement('ul');
      list.className = 'search-suggestions__results-list';

      products.forEach((p) => {
        const li = document.createElement('li');

        const a = document.createElement('a');
        a.className = 'search-suggestions__itemCard';
        a.href = p?.url || `/search?type=product&q=${encodeURIComponent(query)}`;

        const thumb = document.createElement('div');
        thumb.className = 'search-suggestions__thumb';

        const img = this.getImage(p);
        if (img) {
          const i = document.createElement('img');
          i.src = img;
          i.loading = 'lazy';
          i.alt = p?.title || '';
          thumb.appendChild(i);
        }

        const meta = document.createElement('div');

        const title = document.createElement('p');
        title.className = 'search-suggestions__title';
        title.textContent = p?.title || '';
        meta.appendChild(title);

        const descText = this.getDescription(p);
        if (descText) {
          const desc = document.createElement('div');
          desc.className = 'search-suggestions__desc';
          desc.textContent = descText;
          meta.appendChild(desc);
        }

        a.appendChild(thumb);
        a.appendChild(meta);
        li.appendChild(a);
        list.appendChild(li);
      });

      box.appendChild(list);

      const ctaRow = document.createElement('div');
      ctaRow.className = 'search-suggestions__ctaRow';

      const cta = document.createElement('a');
      cta.className = 'search-suggestions__cta';
      cta.href = `/search?type=product&q=${encodeURIComponent(query)}`;
      cta.innerHTML = `Ver todos os resultados <span>↵</span>`;

      ctaRow.appendChild(cta);
      box.appendChild(ctaRow);

      wrapper.appendChild(box);

      this.innerHTML = '';
      this.appendChild(wrapper);
    }
  }

  customElements.define('search-suggestions', SearchSuggestions);
}
